import { httpClient } from "../http-client";
import { ApiError } from "../api-error";
import { extractApiErrorCode } from "../card-gate/card-gate-controller";
import { ApiResponse } from "../../types/responses/api-response";
import { InternalRole } from "../../types/enums/internal-roles.enum";
import { EventRole } from "../../types/enums/event-roles.enum";
import {
  announceRefreshed,
  waitForRefreshAnnouncement,
} from "../auth/refresh-coordination";

// ===== AUTH API =====

export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  // GDPR Art. 7 — версия принятых Условий; backend строго сверяет (@Equals). Обязательно.
  acceptedTermsVersion: string;
  referralCode?: string;
  // AD-169: язык интерфейса, который человек видел на форме регистрации.
  language?: string;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string; // @deprecated Используйте getUserRoles()
    isActive: boolean;
    emailVerified: boolean;
    twoFactorEnabled?: boolean;
    createdAt: string;
    updatedAt: string;
    // Новые поля для ролевой системы
    currentRole?: UserRole;
    availableRoles?: UserRole[];
  };
  accessToken: string;
  refreshToken?: string; // deprecated: теперь в HttpOnly cookie
  expiresIn: number;
  requiresMfa?: false;
}

// FIX-11 (П0.1): вместо полного токена сервер возвращает короткоживущий
// "MFA pending" токен (scope=mfa_pending, exp~5m) — используется для
// завершения логина через POST /auth/mfa/verify.
export interface MfaChallengeResponse {
  requiresMfa: true;
  mfaPendingToken: string;
  message: string;
}

export type LoginResult = AuthResponse | MfaChallengeResponse;

export interface MfaVerifyTokens {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
}

export interface Session {
  id: string;
  userId: string;
  deviceInfo: {
    userAgent: string;
    ip: string;
    location?: string;
  };
  isActive: boolean;
  lastActivity: string;
  createdAt: string;
}

// ===== НОВЫЕ ТИПЫ ДЛЯ РОЛЕВОЙ СИСТЕМЫ =====

export interface UserRole {
  id: string;
  type: "internal" | "event";
  role: InternalRole | EventRole;
  eventId?: string;
  eventName?: string | undefined;
  permissions: string[];
  assignedAt: string;
  assignedBy: string | null;
  isActive: boolean;
}

export interface UserRolesResponse {
  internalRoles: UserRole[];
  eventRoles: UserRole[];
  currentRole: UserRole | null;
  availableRoles: UserRole[];
}

export interface RoleSwitchRequest {
  roleId: string;
  eventId?: string;
}

export interface RoleSwitchResponse {
  success: boolean;
  newRole: UserRole;
  permissions: string[];
}

/** Тип ответа ротации — вынесен, чтобы им же типизировался слот ниже. */
type RefreshResponse = ApiResponse<{ accessToken: string; expiresIn: number }>;

/**
 * Обновление токена, идущее прямо сейчас. Ровно одно на вкладку.
 *
 * ЗАЧЕМ. `/auth/refresh` на бэкенде — РОТАЦИЯ: первый запрос атомарно
 * забирает refresh-токен под себя (AU6, ADR-007 claim-before-act), выдаёт
 * новый и отзывает старый. Второй запрос с ТЕМ ЖЕ токеном приходит к уже
 * отозванной строке и получает 401 `REFRESH_ROTATION_RACE`, а перехватчик
 * `http-client.ts` на 401 стирает токены и уводит на форму входа. Фронт
 * создавал такие пары сам: у `useTokenAutoRefresh` в зависимостях эффекта
 * лежит колбэк `onExpired`, создаваемый заново на каждый рендер
 * `AuthProvider`, а ветка `msUntilRefresh <= 0` зовёт обновление сразу и без
 * стража — в последнюю минуту жизни токена каждый рендер слал ещё один
 * запрос. Слот делает лишние вызовы безвредными: они получают тот же ответ,
 * а в сеть уходит один запрос.
 *
 * МЕЖВКЛАДОЧНЫЙ СЛУЧАЙ (закрыт 06.09.2026). Прежняя редакция этого комментария
 * утверждала, что фронт один его закрыть не может — «признака „ответ победителя
 * уже приехал“ у проигравшей вкладки нет». Признак можно СОЗДАТЬ, не нарушая
 * инвариантов: победитель объявляет факт обновления по BroadcastChannel
 * (`services/auth/refresh-coordination.ts`), проигравший ждёт объявления и
 * повторяет запрос уже с новой cookie. Через канал уходит только отметка
 * времени — ни access-, ни refresh-токен, поэтому «токен живёт в памяти вкладки,
 * refresh — HttpOnly» остаётся в силе. Слепой повтор по таймеру по-прежнему
 * запрещён: он попал бы в `family_compromise` — разлогин на всех устройствах.
 *
 * Уточнение 14.09.2026 (AD-184, фронтовая половина). «Закрыт» выше — о
 * замысле, не об исполнении: до этой даты ветка гонки не исполнялась ни разу —
 * разбор читал `error.response`, которого у `ApiError` нет. Посылку, на которой
 * держится сам замысел — «после объявления победителя в браузере уже лежит его
 * cookie» — модульный тест проверить не может: предмет там хранилище cookie
 * браузера. Её проверили живой пробой в настоящем Chromium
 * (`tests/e2e/journeys/j01d-refresh-rotation-race.spec.ts`, 14.09.2026): повтор
 * проигравшего ушёл с заголовком `Cookie`, равным НОВОМУ значению победителя, и
 * получил 200, а контрольный случай той же пробы — повтор со старым значением —
 * дал 401 без кода гонки и убил следом живую cookie победителя. Поэтому ветка
 * ниже оставлена рабочей, а не заглушённой.
 */
let refreshInFlight: Promise<RefreshResponse> | null = null;

/** Сколько ждём объявления победителя гонки, прежде чем сдаться (мс). */
const ROTATION_RACE_WAIT_MS = 2000;
/** Потолок ожидания при 429, чтобы бутстрап не висел на минуту (мс). */
const RATE_LIMIT_MAX_WAIT_MS = 3000;
/**
 * Пауза при 429, когда `Retry-After` скрипту не виден (сервер перестал
 * перечислять его в `Access-Control-Expose-Headers`) (мс).
 */
const RATE_LIMIT_FALLBACK_WAIT_MS = 700;

/** Код, которым бэкенд помечает проигранную гонку ротации и просит повтор. */
const ROTATION_RACE_CODE = "REFRESH_ROTATION_RACE";

/**
 * Пауза перед повтором после 429, мс. Секунды `Retry-After` кладёт в
 * `details.retryAfterSeconds` сам `httpClient.handleError`.
 */
function retryAfterMs(error: ApiError): number {
  const seconds = error.details?.["retryAfterSeconds"];
  if (typeof seconds !== "number" || seconds <= 0) {
    return RATE_LIMIT_FALLBACK_WAIT_MS;
  }
  return Math.min(seconds * 1000, RATE_LIMIT_MAX_WAIT_MS);
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const postRefresh = (): Promise<RefreshResponse> =>
  httpClient.post<{ accessToken: string; expiresIn: number }>("/auth/refresh");

/**
 * Обновление токена, переживающее два штатных отказа вместо разлогина.
 *
 * ОТКАЗ 1 — проигранная гонка ротации между ВКЛАДКАМИ. Бэкенд отвечает 401
 * `REFRESH_ROTATION_RACE`, семью токенов сохраняет и просит повторить
 * (`auth.service.ts`, ветка `lostRotationRace`). Повторяем ТОЛЬКО после
 * объявления победителя: до него в браузере лежит старая cookie, а повтор с ней
 * попадает уже не в гонку, а в `family_compromise` — разлогин на всех
 * устройствах. Не дождались — честно отдаём ошибку, человек уйдёт на вход.
 *
 * ОТКАЗ 2 — частотный потолок. У `/auth/refresh` стоит
 * `@RateLimit(10, 60_000, { trackBy: 'refresh-cookie', ipLimit: 300 })`: десять
 * на refresh-токен и триста на адрес за минуту. 429 — это «слишком часто», а не
 * «сессии нет»: ждём `Retry-After` (с потолком) и пробуем ещё раз — один раз, —
 * вместо того чтобы стирать живую сессию.
 *
 * Оба отказа приходят `ApiError` из `httpClient.handleError`: статус — в
 * `status`, конверт ADR-002 — в `details.responseBody`, секунды `Retry-After` —
 * в `details.retryAfterSeconds`.
 *
 * Всё прочее — включая 401 без кода — уходит наверх без повторов: это настоящий
 * конец сессии, и притворяться, что он поправим, нельзя.
 */
async function requestRefreshWithRecovery(): Promise<RefreshResponse> {
  try {
    const response = await postRefresh();
    // Новая cookie уже в браузере — можно объявлять: ждущие вкладки повторят.
    announceRefreshed();
    return response;
  } catch (error) {
    if (!(error instanceof ApiError)) throw error;
    const status = error.status;

    if (status === 401 && extractApiErrorCode(error) === ROTATION_RACE_CODE) {
      const winnerFinished = await waitForRefreshAnnouncement(
        ROTATION_RACE_WAIT_MS,
      );
      if (!winnerFinished) throw error;
      const retried = await postRefresh();
      announceRefreshed();
      return retried;
    }

    if (status === 429) {
      await delay(retryAfterMs(error));
      const retried = await postRefresh();
      announceRefreshed();
      return retried;
    }

    throw error;
  }
}

export const authApi = {
  // ===== ОСНОВНЫЕ ENDPOINTS АУТЕНТИФИКАЦИИ =====

  // Вход в систему
  // Ответ — дискриминированный union: либо AuthResponse (requiresMfa отсутствует/false),
  // либо MfaChallengeResponse (requiresMfa: true) для пользователей с включённой MFA
  login: async (data: LoginRequest): Promise<ApiResponse<LoginResult>> => {
    return httpClient.post("/auth/login", data);
  },

  // Регистрация
  register: async (
    data: RegisterRequest,
  ): Promise<ApiResponse<AuthResponse>> => {
    return httpClient.post("/auth/register", data);
  },

  // Выход из системы
  // refreshToken передаётся автоматически через HttpOnly cookie (withCredentials)
  logout: async (): Promise<ApiResponse<void>> => {
    return httpClient.post("/auth/logout");
  },

  // Обновление токена
  // refreshToken передаётся автоматически через HttpOnly cookie (withCredentials)
  // Одновременные вызовы разделяют ОДИН запрос — см. `refreshInFlight` выше.
  refresh: async (): Promise<RefreshResponse> => {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = requestRefreshWithRecovery();
    try {
      return await refreshInFlight;
    } finally {
      // Слот освобождается и на отказе: следующая попытка обязана уйти в сеть,
      // иначе одна сетевая ошибка навсегда запирает обновление токена.
      refreshInFlight = null;
    }
  },

  // Запрос сброса пароля
  forgotPassword: async (
    email: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.post("/auth/forgot-password", { email });
  },

  // Сброс пароля
  // Контракт backend ResetPasswordDto: поле называется `password` (не `newPassword`),
  // иначе whitelist+forbidNonWhitelisted → 400. Параметр оставляем описательным.
  resetPassword: async (
    token: string,
    newPassword: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.post("/auth/reset-password", {
      token,
      password: newPassword,
    });
  },

  // Подтверждение email
  // ВАЖНО: Backend использует GET с параметром в URL
  verifyEmail: async (
    token: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.get(`/auth/verify/${token}`);
  },

  // Повторная отправка письма верификации email.
  // Анти-enumeration: BE всегда отвечает «тихим успехом» (не раскрывает существование email).
  resendVerification: async (
    email: string,
  ): Promise<ApiResponse<{ message?: string }>> => {
    return httpClient.post("/auth/resend-verification", { email });
  },

  // Получение профиля текущего пользователя
  getMe: async (): Promise<ApiResponse<AuthResponse["user"]>> => {
    return httpClient.get("/auth/me");
  },

  // Смена пароля
  changePassword: async (
    currentPassword: string,
    newPassword: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.post("/auth/change-password", {
      currentPassword,
      newPassword,
    });
  },

  // ===== MFA MANAGEMENT (auth/mfa) — ADR-015 =====
  // Единый безопасный путь (AES-секрет + SHA-256 backup-коды). Легаси auth/2fa/*
  // удалён: его писатель был сломан (no-op), а секреты — plaintext-дизайн.
  // Поток управления: setup → confirm → (status) → disable.

  // Начать настройку MFA: получить QR-код и одноразовые backup-коды (показать один раз)
  setupMfa: async (): Promise<
    ApiResponse<{ qrCode: string; recoveryCodes: string[] }>
  > => {
    return httpClient.post("/auth/mfa/setup");
  },

  // Подтвердить первый TOTP-код и активировать MFA
  confirmMfa: async (
    code: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.post("/auth/mfa/confirm", { code });
  },

  // Статус MFA для текущего пользователя
  getMfaStatus: async (): Promise<ApiResponse<{ mfaEnabled: boolean }>> => {
    return httpClient.get("/auth/mfa/status");
  },

  // Отключить MFA (серверный DELETE — код не требуется, достаточно авторизации)
  disableMfa: async (): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete("/auth/mfa/disable");
  },

  // Завершение login-flow для пользователей с включённой MFA (FIX-11, П0.1).
  // ВАЖНО: на этом этапе у пользователя ещё нет access-токена — передаём
  // короткоживущий "MFA pending" токен явным заголовком, в обход обычного
  // axios-интерцептора (который подставляет accessToken из SecureStorage).
  verifyMfaChallenge: async (
    code: string,
    pendingToken: string,
  ): Promise<ApiResponse<MfaVerifyTokens>> => {
    return httpClient.post(
      "/auth/mfa/verify",
      { code },
      { headers: { Authorization: `Bearer ${pendingToken}` } },
    );
  },

  // ===== УПРАВЛЕНИЕ СЕССИЯМИ =====

  // Активные сессии
  getSessions: async (): Promise<ApiResponse<Session[]>> => {
    return httpClient.get("/auth/sessions");
  },

  // Завершение сессии
  terminateSession: async (
    sessionId: string,
  ): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete(`/auth/sessions/${sessionId}`);
  },

  // Выход со всех устройств
  // ВАЖНО: Backend путь /auth/logout/all
  logoutAll: async (): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.post("/auth/logout/all");
  },

  // ===== РОЛЕВАЯ СИСТЕМА ENDPOINTS =====

  // Получение ролей пользователя
  getUserRoles: async (): Promise<ApiResponse<UserRolesResponse>> => {
    // Backend (commit 308ecfe) добавил пагинацию: поле `data` теперь массив ролей,
    // а backward-compat поля (internalRoles, eventRoles, currentRole, availableRoles)
    // возвращаются на верхнем уровне ответа — не внутри `data`.
    const raw = await httpClient.get<any>("/auth/user/roles");
    const r = raw as any;
    return {
      data: {
        internalRoles: r.internalRoles ?? [],
        eventRoles: r.eventRoles ?? [],
        currentRole: r.currentRole ?? null,
        availableRoles: r.availableRoles ?? [],
      },
      success: r.success,
    };
  },

  // Переключение внутренней роли
  switchInternalRole: async (
    roleId: string,
  ): Promise<ApiResponse<{ user: any; token: string }>> => {
    return httpClient.post("/auth/roles/switch/internal", { roleId });
  },

  // Переключение роли события
  switchEventRole: async (
    roleId: string,
    eventId: string,
  ): Promise<ApiResponse<{ user: any; token: string }>> => {
    return httpClient.post("/auth/roles/switch/event", { roleId, eventId });
  },

  // Получение доступных ролей для переключения
  getAvailableRoles: async (): Promise<
    ApiResponse<{ internalRoles: UserRole[]; eventRoles: UserRole[] }>
  > => {
    return httpClient.get("/auth/roles/available");
  },

  // Получение permissions конкретной роли
  getRolePermissions: async (
    roleId: string,
  ): Promise<
    ApiResponse<{ roleId: string; roleName: string; permissions: string[] }>
  > => {
    return httpClient.get(`/auth/roles/${roleId}/permissions`);
  },

  // Проверка доступа к ресурсу
  checkPermission: async (
    permission: string,
  ): Promise<ApiResponse<{ hasAccess: boolean }>> => {
    return httpClient.post("/auth/user/check-permission", { permission });
  },

  // ===== GDPR ENDPOINTS =====

  // GDPR Right to Access (Article 15) - Экспорт данных пользователя (Backend: POST /gdpr/export-data)
  //
  // Ответ — НЕ сами данные, а ссылка на них: бэкенд собирает выгрузку, кладёт её
  // в приватный бакет GDPR-экспортов и возвращает presigned-ссылку с конечным
  // сроком жизни (`gdpr.controller.ts:220-237`). Тип был `Record<string,
  // unknown>`, и вызывающий код принимал конверт за данные — пользователь
  // скачивал файл с двумя служебными полями вместо своей выгрузки.
  exportUserData: async (): Promise<
    ApiResponse<{ exportUrl: string; expiresAt: string }>
  > => {
    return httpClient.post("/gdpr/export-data");
  },

  // GDPR Right to Erasure (Article 17) - Удаление аккаунта (Backend: DELETE /gdpr/account)
  deleteAccount: async (options?: {
    exportDataFirst?: boolean;
    reason?: string;
  }): Promise<ApiResponse<{ message: string }>> => {
    return httpClient.delete("/gdpr/account", { data: options ?? {} });
  },
};

export default authApi;
